import type { KeyboardEvent, ReactNode } from "react";
import { Link, type To } from "react-router";

import { cx } from "@/vnext/shared/cx";

export interface WorkspaceTab<TPath extends string = string> {
  count?: ReactNode;
  hidden?: boolean;
  label: string;
  path: TPath;
  unavailable?: boolean;
  unavailableLabel?: string;
}

interface WorkspaceTabNavProps<TTab extends WorkspaceTab> {
  activePath: TTab["path"] | string;
  ariaLabel: string;
  buildPath?: (tab: TTab) => To;
  className?: string;
  onTabClick?: (tab: TTab) => void;
  tabs: readonly TTab[];
}

export const WorkspaceTabNav = <TTab extends WorkspaceTab>({
  activePath,
  ariaLabel,
  buildPath,
  className,
  onTabClick,
  tabs,
}: WorkspaceTabNavProps<TTab>) => {
  const visibleTabs = tabs.filter((tab) => !tab.hidden);

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={cx("admin-workspace-tabs", className)}
    >
      {visibleTabs.map((tab) => {
        const isActive = activePath === tab.path;
        const unavailableLabel = tab.unavailableLabel ?? "Unavailable";

        return (
          <Link
            aria-current={isActive ? "page" : undefined}
            aria-disabled={tab.unavailable ? true : undefined}
            className={cx(
              "admin-workspace-tabs__item",
              isActive && "is-active",
              tab.unavailable && "is-unavailable",
            )}
            key={tab.path}
            onClick={(event) => {
              if (tab.unavailable) {
                event.preventDefault();
                return;
              }

              onTabClick?.(tab);
            }}
            to={buildPath ? buildPath(tab) : tab.path}
          >
            <span>{tab.label}</span>
            {tab.count !== undefined ? " " : null}
            {tab.count !== undefined ? (
              <span className="admin-workspace-tabs__count">{tab.count}</span>
            ) : null}
            {tab.unavailable ? " " : null}
            {tab.unavailable ? (
              <span className="admin-workspace-tabs__state">
                {unavailableLabel}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
};

export interface WorkspaceStateTab<TId extends string = string> {
  count?: ReactNode;
  hidden?: boolean;
  id: TId;
  label: string;
  unavailable?: boolean;
  unavailableLabel?: string;
}

interface WorkspaceStateTabListProps<TTab extends WorkspaceStateTab> {
  activeTabId: TTab["id"] | string;
  ariaLabel: string;
  baseId: string;
  onSelect: (tabId: TTab["id"]) => void;
  tabs: readonly TTab[];
}

const getWorkspaceStateTabId = (baseId: string, tabId: string): string =>
  `${baseId}-${tabId}-tab`;

const getWorkspaceStateTabPanelId = (baseId: string, tabId: string): string =>
  `${baseId}-${tabId}-panel`;

export const WorkspaceStateTabList = <TTab extends WorkspaceStateTab>({
  activeTabId,
  ariaLabel,
  baseId,
  onSelect,
  tabs,
}: WorkspaceStateTabListProps<TTab>) => {
  const visibleTabs = tabs.filter((tab) => !tab.hidden);
  const selectableTabs = visibleTabs.filter((tab) => !tab.unavailable);
  const selectByIndex = (index: number) => {
    const nextTab = selectableTabs.find((_tab, tabIndex) => tabIndex === index);
    if (nextTab) {
      onSelect(nextTab.id);
      document
        .getElementById(getWorkspaceStateTabId(baseId, nextTab.id))
        ?.focus();
    }
  };
  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    tab: TTab,
  ) => {
    const currentIndex = selectableTabs.findIndex(
      (selectableTab) => selectableTab.id === tab.id,
    );

    if (currentIndex === -1) {
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      selectByIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      selectByIndex(selectableTabs.length - 1);
      return;
    }

    const delta =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (delta === 0) {
      return;
    }

    event.preventDefault();
    selectByIndex(
      (currentIndex + delta + selectableTabs.length) % selectableTabs.length,
    );
  };

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <div aria-label={ariaLabel} className="admin-workspace-tabs" role="tablist">
      {visibleTabs.map((tab) => {
        const isActive = activeTabId === tab.id;
        const unavailableLabel = tab.unavailableLabel ?? "Unavailable";

        return (
          <button
            aria-controls={getWorkspaceStateTabPanelId(baseId, tab.id)}
            aria-disabled={tab.unavailable ? true : undefined}
            aria-selected={isActive}
            className={cx(
              "admin-workspace-tabs__item",
              isActive && "is-active",
              tab.unavailable && "is-unavailable",
            )}
            id={getWorkspaceStateTabId(baseId, tab.id)}
            key={tab.id}
            onClick={() => {
              if (!tab.unavailable) {
                onSelect(tab.id);
              }
            }}
            onKeyDown={(event) => handleKeyDown(event, tab)}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            type="button"
          >
            <span>{tab.label}</span>
            {tab.count !== undefined ? " " : null}
            {tab.count !== undefined ? (
              <span className="admin-workspace-tabs__count">{tab.count}</span>
            ) : null}
            {tab.unavailable ? " " : null}
            {tab.unavailable ? (
              <span className="admin-workspace-tabs__state">
                {unavailableLabel}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

interface WorkspaceStateTabPanelProps<TId extends string = string> {
  activeTabId: TId | string;
  baseId: string;
  children: ReactNode;
  className?: string;
  mountInactive?: boolean;
  tabId: TId;
}

export const WorkspaceStateTabPanel = <TId extends string = string>({
  activeTabId,
  baseId,
  children,
  className,
  mountInactive = false,
  tabId,
}: WorkspaceStateTabPanelProps<TId>) => {
  const isActive = activeTabId === tabId;

  return (
    <section
      aria-labelledby={getWorkspaceStateTabId(baseId, tabId)}
      className={className}
      hidden={!isActive}
      id={getWorkspaceStateTabPanelId(baseId, tabId)}
      role="tabpanel"
      tabIndex={isActive ? 0 : -1}
    >
      {isActive || mountInactive ? children : null}
    </section>
  );
};
